// @tier: PREMIUM — Historique/replay GPS
import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { format, parseISO, differenceInMinutes, isBefore, subDays } from 'date-fns';
import { useFeatureFlags } from './useFeatureFlags';
import { DATA_RETENTION } from '../config/constants';
import apiService from '../services/api';
import logger from '@/utils/logger';
import { createTripMapUrl } from '../config/mapboxConfig';

export function calculateDuration(start, end) {
  if (!start || !end) return '-';
  const minutes = differenceInMinutes(parseISO(end), parseISO(start));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

export const useHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentSubscription } = useFeatureFlags();
  const queryParams = new URLSearchParams(location.search);

  const [dateFilter, setDateFilter] = useState(queryParams.get('date') || format(new Date(), 'yyyy-MM-dd'));
  const [routeFilter, setRouteFilter] = useState(queryParams.get('route_id') || 'all');
  const [busFilter, setBusFilter] = useState(queryParams.get('bus_id') || 'all');

  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [buses, setBuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripDetails, setTripDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [tripMapUrl, setTripMapUrl] = useState(null);
  const [tripStats, setTripStats] = useState(null);
  const [tripPositions, setTripPositions] = useState([]);
  const [tripMapStops, setTripMapStops] = useState([]);

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const [routesData, busesData] = await Promise.all([
          apiService.get('/admin/routes?status=active'),
          apiService.get('/admin/buses'),
        ]);
        setRoutes(routesData.data || []);
        setBuses(busesData.data || []);
      } catch (error) {
        logger.error('Erreur chargement ressources:', error);
      }
    };
    fetchResources();
  }, []);

  useEffect(() => {
    const fetchTrips = async () => {
      setLoading(true);
      try {
        let url = `/admin/tracking/trips?limit=100&status=completed,active,in_progress`;
        if (dateFilter) url += `&date=${dateFilter}`;
        if (routeFilter && routeFilter !== 'all') url += `&route_id=${routeFilter}`;
        if (busFilter && busFilter !== 'all') url += `&bus_id=${busFilter}`;
        const response = await apiService.get(url);
        setTrips(response.data || []);
      } catch (error) {
        logger.error('Erreur chargement historique:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchTrips();
  }, [dateFilter, routeFilter, busFilter]);

  useEffect(() => {
    if (!selectedTrip) {
      setTripDetails(null);
      setTripMapUrl(null);
      setTripStats(null);
      return;
    }
    const fetchDetails = async () => {
      setLoadingDetails(true);
      setTripMapUrl(null);
      setTripStats(null);
      try {
        const [attendanceRes, trailRes] = await Promise.all([
          apiService.get(`/admin/tracking/trips/${selectedTrip.id}/attendance`),
          apiService.get(`/admin/tracking/trips/${selectedTrip.id}/gps-trail`),
        ]);
        const attendanceData = attendanceRes.data;
        setTripDetails(attendanceData);

        // Stops stats depuis l'attendance (source de vérité — trip_stops peut être vide)
        const attendanceStops = attendanceData?.stops || [];
        const stopsCount = attendanceStops.length;
        const stopsVisited = attendanceStops.filter((s) => s.status === 'completed' || s.status === 'visited').length;

        // Stops depuis l'attendance : source fiable avec coordonnées
        const mapStops = attendanceStops
          .filter((s) => s.latitude != null && s.longitude != null)
          .map((s, i) => ({
            lat: parseFloat(s.latitude),
            lng: parseFloat(s.longitude),
            name: s.stopName || s.stop_name || '',
            order: s.stopOrder ?? i + 1,
            status: s.status,
            markedAt: s.markedAt ?? s.actualDeparture ?? s.actualArrival ?? null,
          }));

        const trail = trailRes.data;
        logger.debug(
          `[HISTORY][DEBUG] gps-trail response — positions: ${trail?.positions?.length ?? 'null'} stops: ${trail?.stops?.length ?? 0}`,
        );

        if (trail?.positions?.length >= 2) {
          setTripPositions(trail.positions);
          setTripMapStops(mapStops);
          // URL statique conservée comme fallback SSR/impression
          const mapUrl = createTripMapUrl(trail.positions, mapStops);
          setTripMapUrl(mapUrl);
        } else {
          setTripPositions([]);
          setTripMapStops([]);
          setTripMapUrl(null);
          logger.warn(`[HISTORY][DEBUG] Positions insuffisantes: ${trail?.positions?.length ?? 0}`);
        }
        setTripStats(
          trail?.stats
            ? { ...trail.stats, stopsCount, stopsVisited }
            : { distanceKm: 0, durationMin: 0, avgSpeedKmh: 0, maxSpeedKmh: 0, stopsCount, stopsVisited },
        );
      } catch (error) {
        logger.error('Erreur chargement détails trajet:', error);
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [selectedTrip]);

  // GPS expiré : Basic + trip créé il y a plus de 30 jours → la carte ne s'affiche pas
  const isGpsExpired =
    selectedTrip != null &&
    currentSubscription === 'basic' &&
    isBefore(parseISO(selectedTrip.created_at), subDays(new Date(), DATA_RETENTION.GPS_BASIC_DAYS));

  const handleTripClick = (trip) => setSelectedTrip(trip);

  const resetFilters = () => {
    setDateFilter(format(new Date(), 'yyyy-MM-dd'));
    setRouteFilter('all');
    setBusFilter('all');
  };

  return {
    navigate,
    dateFilter,
    setDateFilter,
    routeFilter,
    setRouteFilter,
    busFilter,
    setBusFilter,
    trips,
    routes,
    buses,
    loading,
    selectedTrip,
    setSelectedTrip,
    tripDetails,
    loadingDetails,
    tripMapUrl,
    tripStats,
    tripPositions,
    tripMapStops,
    isGpsExpired,
    handleTripClick,
    resetFilters,
  };
};

export default useHistory;
